<template>
  <a-modal
    :open="open"
    :destroy-on-close="true"
    title="Configurar límite de consumo"
    width="560px"
    @cancel="requestClose"
  >
    <div class="section">
      <h4 class="section__title">Notificación de límite de consumo</h4>
      <p class="section__description">
        Establece el monto límite de consumo de API calls para recibir una notificación cuando lo
        alcances.
      </p>

      <div class="limit">
        <span class="limit__required">*</span>
        <span class="limit__label">Monto límite</span>
        <a-tooltip
          :title="`Recibe una notificación cuando el consumo de tus API calls de cobro alcance el monto límite configurado. Monto mínimo: ${formatMoney(MIN_LIMIT_AMOUNT)}`"
        >
          <InfoCircleOutlined class="limit__info" />
        </a-tooltip>
        <span class="limit__label">:</span>
        <a-input-number
          v-model:value="amount"
          class="limit__input"
          prefix="$"
          :min="MIN_LIMIT_AMOUNT"
          :precision="2"
        />
        <span v-if="equivalentCalls !== null" class="limit__equivalent">
          Equivale a {{ formatCalls(equivalentCalls) }} API calls
        </span>
      </div>

      <a-alert
        v-if="showLowerLimitNotice"
        class="notice"
        type="warning"
        show-icon
        message="El nuevo límite es menor al actual monto facturado. Este nuevo límite se aplicará al inicio del próximo ciclo de facturación."
      />
    </div>

    <div class="section">
      <h4 class="section__title">Configuración de consumo por límite</h4>
      <a-radio-group v-model:value="mode" class="modes">
        <a-radio value="stop" class="modes__option">
          <span class="modes__title">Detener consumo al llegar al límite</span>
          <span class="modes__description">
            Cuando alcances tu límite de consumo, se dejarán de consumir API calls.
          </span>
        </a-radio>
        <a-alert
          v-if="showBilledAboveLimitNotice"
          class="notice notice--inline"
          type="warning"
          show-icon
          :message="`El actual monto facturado (${formatMoney(billedAmount)}) es mayor al límite de consumo. Aumenta el límite para guardar esta configuración.`"
        />
        <a-radio value="continue" class="modes__option">
          <span class="modes__title">Continuar consumo después de llegar al límite</span>
          <span class="modes__description">
            Cuando alcances tu límite de consumo, se continuarán consumiendo API calls y estas se
            añadirán en la facturación de tu plan.
          </span>
        </a-radio>
      </a-radio-group>
    </div>

    <template #footer>
      <a-button @click="requestClose">Cancelar</a-button>
      <a-tooltip :title="isDirty ? '' : 'Sin cambios por guardar'">
        <span class="footer__save">
          <a-button
            type="primary"
            :disabled="saveDisabled || saving"
            :loading="saving"
            @click="handleSave"
          >
            Guardar cambios
          </a-button>
        </span>
      </a-tooltip>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { Modal, message } from "ant-design-vue";
import { InfoCircleOutlined } from "@ant-design/icons-vue";

import { apiService } from "@/app/platformSettings/services/ApiService";
import type {
  ApiCloudLimit,
  ApiCloudLimitMode,
  ApiCloudLimitPayload,
} from "@/@types/platformSettings/apiCloudLimit";

const props = withDefaults(
  defineProps<{
    open: boolean;
    price: number | null;
    billedAmount?: number | null;
    initialAmount?: number | null;
    initialMode?: ApiCloudLimitMode;
  }>(),
  {
    billedAmount: null,
    initialAmount: null,
    initialMode: "stop",
  }
);

const emit = defineEmits<{
  (e: "update:open", value: boolean): void;
  (e: "save", limit: ApiCloudLimit): void;
}>();

const MIN_LIMIT_AMOUNT = 5;
const DEFAULT_LIMIT_AMOUNT = 20;

// Sin límite previo, el modal parte de un monto por defecto.
const baseAmount = computed(() => props.initialAmount ?? DEFAULT_LIMIT_AMOUNT);

const amount = ref<number | null>(baseAmount.value);
const mode = ref<ApiCloudLimitMode>(props.initialMode);

// `PUT /api/v1/api-cloud/limit/` en vuelo: deshabilita y bloquea el botón de guardado.
const saving = ref(false);

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return;

    amount.value = baseAmount.value;
    mode.value = props.initialMode;
  }
);

// Equivalencia `n = monto / precio`. El precio llega como prop desde quien invoca el modal
// (tomado de `overage_price`); nunca se usa un literal en el componente.
const equivalentCalls = computed(() => {
  if (amount.value === null || amount.value <= 0) return null;
  if (!props.price) return null;

  return Math.round(amount.value / props.price);
});

const formatCalls = (value: number) => value.toLocaleString("en-US");

const formatMoney = (value: number | null) =>
  value === null ? "" : `$${value.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

const amountIsValid = computed(() => amount.value !== null && amount.value >= MIN_LIMIT_AMOUNT);

const amountChanged = computed(() => amount.value !== baseAmount.value);
const modeChanged = computed(() => mode.value !== props.initialMode);
// Sin límite previo no hay nada guardado: el monto por defecto también se puede guardar.
const hasSavedLimit = computed(() => props.initialAmount !== null);
const hasEdits = computed(() => amountChanged.value || modeChanged.value);
const isDirty = computed(() => !hasSavedLimit.value || hasEdits.value);

const isBelowBilled = computed(
  () =>
    amountIsValid.value &&
    props.billedAmount !== null &&
    (amount.value as number) < props.billedAmount
);

// Editar el monto por debajo de lo facturado se permite: aplica desde el próximo ciclo.
const showLowerLimitNotice = computed(() => isBelowBilled.value && !modeChanged.value);

// Cambiar a "Detener" con un monto facturado mayor al límite bloquea el guardado.
const showBilledAboveLimitNotice = computed(
  () => isBelowBilled.value && modeChanged.value && mode.value === "stop"
);

const saveDisabled = computed(
  () => !amountIsValid.value || !isDirty.value || showBilledAboveLimitNotice.value
);

const closeModal = () => emit("update:open", false);

const requestClose = () => {
  if (!hasEdits.value) {
    closeModal();
    return;
  }

  Modal.confirm({
    title: "¿Deseas descartar los cambios realizados?",
    content: "Al descartar, no se quedarán guardados los cambios realizados en la configuración.",
    okText: "Sí, descartar",
    cancelText: "No, seguir editando",
    onOk: closeModal,
  });
};

const handleSave = () => {
  if (saveDisabled.value || saving.value) return;

  Modal.confirm({
    title: "Estás a punto de cambiar la configuración de consumo de API calls, ¿deseas continuar?",
    content: "Se aplicarán todos los cambios realizados en la configuración del límite y consumo de API calls.",
    okText: "Sí, continuar",
    cancelText: "No, seguir editando",
    onOk: () => persist(),
  });
};

const persist = async () => {
  saving.value = true;
  // La UI tiene un solo monto (`Monto límite`), que es a la vez el límite y el umbral de
  // notificación (el `Notificar en $X` del modo continuar).
  const payload: ApiCloudLimitPayload = {
    amount_usd: amount.value as number,
    notify_amount_usd: amount.value as number,
    mode: mode.value,
  };

  try {
    const saved = await apiService.updateApiCloudLimit(payload);
    // Solo se confirma cuando el backend persistió la configuración.
    message.success("Cambios guardados con éxito");
    emit("save", saved);
    closeModal();
  } catch {
    // Mensaje genérico del repo: `ResponseError.getErrorMessage()` stringifica el cuerpo cuando
    // no hay `errors`, así que un 404 de Django imprimiría la página HTML completa. El detalle
    // técnico no se expone al usuario.
    message.error("Ha ocurrido un error");
    // Ante un fallo el modal permanece abierto para reintentar.
  } finally {
    saving.value = false;
  }
};
</script>

<style scoped lang="sass">
.section
  margin-bottom: 20px

  &__title
    margin-bottom: 4px
    font-size: 14px
    font-weight: 600
    color: $gray-9

  &__description
    margin-bottom: 12px
    font-size: 13px
    color: $gray-7

.limit
  display: flex
  align-items: center
  gap: 8px

  &__required
    color: $red-6

  &__label
    font-size: 14px
    color: $gray-9

  &__info
    color: $gray-7

  &__input
    width: 140px

  &__equivalent
    font-size: 13px
    color: $gray-7

.modes
  display: flex
  flex-direction: column
  gap: 12px

  &__option
    display: flex
    align-items: flex-start
    white-space: normal

  &__title
    display: block
    font-size: 14px
    color: $gray-9

  &__description
    display: block
    margin-top: 2px
    font-size: 12px
    color: $gray-7

.notice
  margin-top: 12px

  &--inline
    margin: 0 0 0 24px

.footer__save
  display: inline-block
  margin-left: 8px
</style>

